import type { google } from '@alugha/ima';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { SpeakerHighIcon } from '@phosphor-icons/react/SpeakerHigh';
import { SpeakerSlashIcon } from '@phosphor-icons/react/SpeakerSlash';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { saveVolume } from '../imaSdkUtils.ts';
import { rangeInputSx } from './rangeInputSx.ts';

type VolumeControlProps = {
  volume: number;
  manager: google.ima.AdsManager | null;
  setVolume: (newVolume: number) => void;
  prevVolumeRef: React.MutableRefObject<number>;
  compact?: boolean;
};

export function VolumeControl({ volume, manager, setVolume, prevVolumeRef, compact = false }: VolumeControlProps) {
  const { t } = useTranslation();

  const handleMuteClick = () => {
    if (!manager) {
      return;
    }

    if (volume > 0) {
      prevVolumeRef.current = volume;
      setVolume(0);
      manager.setVolume(0);
    } else {
      const restored = prevVolumeRef.current || 1;

      setVolume(restored);
      manager.setVolume(restored);
      saveVolume(restored);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!manager) {
      return;
    }

    const newVolume = parseFloat(e.target.value);

    if (newVolume > 0) {
      prevVolumeRef.current = newVolume;
    }

    setVolume(newVolume);
    manager.setVolume(newVolume);
    saveVolume(newVolume);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...(compact && {
          '&:hover .volume-slider': {
            width: '80px',
            opacity: 1,
          },
        }),
      }}
    >
      <IconButton
        size="small"
        onClick={handleMuteClick}
        aria-label={volume === 0 ? t('Unmute') : t('Mute')}
        sx={{ color: theme => theme.palette.common.white }}
      >
        {volume === 0 ? <SpeakerSlashIcon /> : <SpeakerHighIcon />}
      </IconButton>

      <Box
        className="volume-slider"
        component="input"
        type="range"
        value={volume}
        min={0}
        max={1}
        step={0.05}
        aria-label={t('Volume')}
        onChange={handleSliderChange}
        style={{
          background: `linear-gradient(to right, white ${volume * 100}%, rgba(255,255,255,0.3) ${volume * 100}%)`,
        }}
        sx={{
          ...rangeInputSx,
          ...(compact
            ? {
                width: '0px',
                opacity: 0,
                transition: 'width 0.2s ease, opacity 0.2s ease',
                overflow: 'hidden',
              }
            : {
                width: '80px',
              }),
        }}
      />
    </Box>
  );
}
